/**
 * Toast Context with Deduplication
 * Extracted from App.js - Phase 8A Context Refactor
 */
import { createContext, useContext, useState, useCallback, useRef } from "react";

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within ToastProvider");
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const lastToastRef = useRef({});

  const addToast = useCallback((message, type = "success", key = null) => {
    // Defensive: never let a raw API error shape (e.g. FastAPI's
    // `detail` array of validation objects) reach the DOM as a React
    // child — that throws "Objects are not valid as a React child" and
    // crashes the entire app to the global error boundary.
    let safeMessage = message;
    if (typeof safeMessage !== "string") {
      if (Array.isArray(safeMessage)) {
        safeMessage = safeMessage
          .map((it) => (it && typeof it === "object")
            ? (it.msg || JSON.stringify(it))
            : String(it))
          .join(" ");
      } else if (safeMessage && typeof safeMessage === "object") {
        safeMessage = safeMessage.msg || safeMessage.detail || JSON.stringify(safeMessage);
      } else {
        safeMessage = String(safeMessage ?? "Something went wrong.");
      }
    }

    // Deduplicate: if same key shown in last 3 seconds, skip
    const toastKey = key || `${type}_${safeMessage}`;
    const now = Date.now();
    if (lastToastRef.current[toastKey] && now - lastToastRef.current[toastKey] < 3000) {
      return; // Skip duplicate
    }
    lastToastRef.current[toastKey] = now;

    const id = now;
    setToasts(prev => [...prev, { id, message: safeMessage, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);

  const success = useCallback((message, key) => addToast(message, "success", key), [addToast]);
  const error = useCallback((message, key) => addToast(message, "error", key), [addToast]);
  const info = useCallback((message, key) => addToast(message, "info", key), [addToast]);

  const clearDuplicateKey = useCallback((key) => {
    delete lastToastRef.current[key];
  }, []);

  return (
    <ToastContext.Provider value={{ success, error, info, clearDuplicateKey }}>
      {children}
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast toast-${toast.type}`}>
            {toast.type === "success" && "✓ "}
            {toast.type === "error" && "✗ "}
            {toast.type === "info" && "ℹ "}
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
